import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  BaseAssignmentResponseDtoStatus,
  CreateHomeworkAssignmentRequestBodyGenerator,
} from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { BookText, FileText, List, Youtube, GraduationCap } from "lucide-react";
import youtubeLogo from "@packages/assets/youtube.png";
import driveLogo from "@packages/assets/googledrive.png";
import assignmentsBanner from "@packages/assets/banners/assignments.png";

interface GenerationOption {
  type: CreateHomeworkAssignmentRequestBodyGenerator;
  title: string;
  icon: React.ReactNode;
  imageSrc?: string;
  description: string;
}

const AllAssignmentsHeader = ({
  activeFilter,
  setActiveFilter,
}: {
  activeFilter: BaseAssignmentResponseDtoStatus | undefined;
  setActiveFilter: (
    filter: BaseAssignmentResponseDtoStatus | undefined,
  ) => void;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent: track } = trackEvent();
  const [hoveredOption, setHoveredOption] =
    useState<CreateHomeworkAssignmentRequestBodyGenerator | null>(null);

  const generationOptions: GenerationOption[] = [
    {
      type: "YOUTUBE_LINK",
      title: t("assignments.generator.youtube.title"),
      icon: <Youtube className="w-6 h-6" />,
      imageSrc: youtubeLogo,
      description: t("assignments.generator.youtube.description"),
    },
    {
      type: "DRIVE_FILE",
      title: t("assignments.generator.file.title"),
      icon: <FileText className="w-6 h-6" />,
      imageSrc: driveLogo,
      description: t("assignments.generator.file.description"),
    },
  ];

  const handleGenerationClick = (option: GenerationOption) => {
    track(
      {
        category: "ContentGeneration",
        action: "assignment.generate",
        label: "Assignment generation click",
        nonInteraction: false,
      },
      {
        location: "/assignment/generate",
        element: "generate-button",
        assignmentGenerationType: option.type,
      },
    );
    navigate(`/assignments/generate/${option.type.toLowerCase()}`);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start mb-4 md:mb-8">
        <div className="">
          <Text variant="h1">assignments.title</Text>
          <Text variant="p" className="text-gray-600 dark:text-gray-400 mt-1">
            assignments.subtitle
          </Text>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 mt-4 md:mt-0">
          <Button
            size="md"
            rounded="lg"
            bgColor={
              !activeFilter
                ? "bg-dark-500 dark:bg-dark-500"
                : "bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-500"
            }
            textColor={
              !activeFilter
                ? "text-white dark:text-white"
                : "text-gray-700 dark:text-gray-300"
            }
            className={`${
              !activeFilter
                ? "border-dark-400 dark:border-dark-400"
                : "border-gray-300 dark:border-dark-400"
            }`}
            onClick={() => setActiveFilter(undefined)}
          >
            {t("assignments.filters.all")}
          </Button>
          <Button
            size="md"
            rounded="lg"
            bgColor={
              activeFilter === "DRAFT"
                ? "bg-dark-500 dark:bg-dark-500"
                : "bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-500"
            }
            textColor={
              activeFilter === "DRAFT"
                ? "text-white dark:text-white"
                : "text-gray-700 dark:text-gray-300"
            }
            className={`${
              activeFilter === "DRAFT"
                ? "border-dark-400 dark:border-dark-400"
                : "border-gray-300 dark:border-dark-400"
            }`}
            onClick={() =>
              activeFilter !== "DRAFT"
                ? setActiveFilter("DRAFT")
                : setActiveFilter(undefined)
            }
          >
            {t("assignments.filters.draft")}
          </Button>
          <Button
            size="md"
            rounded="lg"
            bgColor={
              activeFilter === "ASSIGNED"
                ? "bg-dark-500 dark:bg-dark-500"
                : "bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-500"
            }
            textColor={
              activeFilter === "ASSIGNED"
                ? "text-white dark:text-white"
                : "text-gray-700 dark:text-gray-300"
            }
            className={`${
              activeFilter === "ASSIGNED"
                ? "border-dark-400 dark:border-dark-400"
                : "border-gray-300 dark:border-dark-400"
            }`}
            onClick={() =>
              activeFilter !== "ASSIGNED"
                ? setActiveFilter("ASSIGNED")
                : setActiveFilter(undefined)
            }
          >
            {t("assignments.filters.assigned")}
          </Button>
          {/* <Button
            size="md"
            rounded="lg"
            bgColor={
              activeFilter === "PENDING"
                ? "bg-indigo-100 dark:bg-indigo-900"
                : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            }
            textColor={
              activeFilter === "PENDING"
                ? "text-indigo-700 dark:text-indigo-100"
                : "text-gray-700 dark:text-gray-300"
            }
            variant="outline"
            className={`${
              activeFilter === "PENDING"
                ? "border-indigo-300 dark:border-indigo-700"
                : "border-gray-300 dark:border-gray-700"
            }`}
            onClick={() =>
              activeFilter !== "PENDING"
                ? setActiveFilter("PENDING")
                : setActiveFilter(undefined)
            }
          >
            {t("assignments.filters.pending")}
          </Button> */}
        </div>
      </div>

      {/* Banner Section */}
      <div className="mb-6">
        <div className="relative overflow-hidden rounded-2xl border border-dark-200 dark:border-dark-400 bg-white dark:bg-dark-600">
          {/* Gradient background overlay */}
          <div
            className="absolute inset-0 pointer-events-none z-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(0, 102, 255, 0.10) 0%, rgba(0, 0, 0, 0.85) 100%)",
            }}
          />
          <div className="flex flex-col md:flex-row h-full p-4 items-center relative z-10">
            {/* Left image */}
            <div className="md:w-2/6 w-full h-28 relative flex items-center justify-start">
              <img
                src={assignmentsBanner}
                alt=""
                className="w-full h-full object-contain mb-2"
                style={{ position: "static" }}
              />
            </div>
            {/* Right text */}
            <div className="flex-1 p-5 sm:p-6 lg:p-8 flex items-end justify-end">
              <div>
                <Text
                  variant="p"
                  className="text-gray-700 dark:text-gray-200 text-sm sm:text-base lg:text-lg leading-relaxed"
                >
                  Manage your <b>assignments</b> efficiently
                  <br />
                  Create, edit, and track student progress!
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {generationOptions.map((option) => {
          const isHovered = hoveredOption === option.type;

          return (
            <button
              key={option.type}
              onClick={() => handleGenerationClick(option)}
              onMouseEnter={() => setHoveredOption(option.type)}
              onMouseLeave={() => setHoveredOption(null)}
              className={`
                relative w-full p-8 rounded-2xl border border-gray-200 dark:border-dark-400
                bg-white dark:bg-dark-900 hover:bg-gray-50 dark:hover:bg-dark-600 
                transition-all duration-300 ease-out
                group overflow-hidden cursor-pointer
                ${isHovered ? "scale-[1.01] shadow-xl border-gray-300 dark:border-dark-300" : "shadow-sm hover:shadow-lg"}
              `}
            >
              {/* Content */}
              <div className="relative z-10 flex items-start space-x-6">
                {/* Logo/Icon */}
                <div className="flex-shrink-0">
                  {option.imageSrc ? (
                    <img
                      src={option.imageSrc}
                      alt={option.title}
                      className="w-16 h-16 rounded-2xl object-contain bg-gray-50 dark:bg-dark-400 p-3 shadow-sm group-hover:shadow-md transition-shadow duration-300"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-dark-400 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      {option.icon}
                    </div>
                  )}
                </div>

                {/* Text content */}
                <div className="flex-1 min-w-0 text-left">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 leading-tight text-left">
                    {option.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed text-left">
                    {option.description}
                  </p>
                </div>

                {/* Arrow indicator */}
                <div className="flex-shrink-0 mt-1">
                  <div
                    className={`
                    w-8 h-8 rounded-full bg-gray-100 dark:bg-dark-400 
                    flex items-center justify-center
                    transition-all duration-300 ease-out
                    ${isHovered ? "bg-gray-200 dark:bg-dark-300 scale-110" : ""}
                  `}
                  >
                    <svg
                      className={`w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform duration-300 ${
                        isHovered ? "translate-x-0.5" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Subtle glow effect on hover */}
              <div
                className={`
                absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-pink-500/0
                transition-all duration-300
                ${isHovered ? "from-blue-500/5 via-purple-500/5 to-pink-500/5" : ""}
              `}
              />
            </button>
          );
        })}
      </div>
    </>
  );
};

export default AllAssignmentsHeader;
